import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const empty = {
  church_name_es: "", church_name_en: "", tagline_es: "", tagline_en: "",
  address: "", phone: "", email: "", service_times_es: "", service_times_en: "",
  instagram: "", facebook: "", youtube: "", tiktok: "", whatsapp: "",
  zelle_enabled: true, zelle_phone: "", zelle_email: "", zelle_name: "",
  zelle_instructions_es: "", zelle_instructions_en: "",
  stripe_enabled: true,
  notification_recipients: "",
  survey_enabled: false, survey_url: "",
  survey_title_es: "", survey_title_en: "",
  survey_description_es: "", survey_description_en: "",
  survey_cta_es: "", survey_cta_en: "",
};

export default function AdminSettings() {
  const { t } = useI18n();
  const [s, setS] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/settings"); setS({ ...empty, ...data }); } catch (_) {} })();
  }, []);

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success(t.admin.saved);
    } catch (_) {
      toast.error(t.common.error);
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setBool = (k) => (val) => setS({ ...s, [k]: val });

  return (
    <div className="space-y-4">
      <h1 className="font-serif-display text-2xl font-semibold">{t.admin.settings}</h1>
      <Card className="rounded-2xl ring-1 ring-black/5">
        <CardContent className="p-4 space-y-4">
          <Group title="Iglesia">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.churchNameEs}><Input value={s.church_name_es} onChange={set("church_name_es")} data-testid="settings-church-name-es" /></Field>
              <Field label={t.admin.settingsForm.churchNameEn}><Input value={s.church_name_en} onChange={set("church_name_en")} data-testid="settings-church-name-en" /></Field>
              <Field label={t.admin.settingsForm.taglineEs}><Input value={s.tagline_es} onChange={set("tagline_es")} data-testid="settings-tagline-es" /></Field>
              <Field label={t.admin.settingsForm.taglineEn}><Input value={s.tagline_en} onChange={set("tagline_en")} data-testid="settings-tagline-en" /></Field>
            </div>
          </Group>
          <Group title={t.social.contact}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.address}><Input value={s.address} onChange={set("address")} data-testid="settings-address" /></Field>
              <Field label={t.admin.settingsForm.phone}><Input value={s.phone} onChange={set("phone")} data-testid="settings-phone" /></Field>
              <Field label={t.admin.settingsForm.email}><Input value={s.email} onChange={set("email")} data-testid="settings-email" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.serviceEs}><Textarea rows={3} value={s.service_times_es} onChange={set("service_times_es")} data-testid="settings-service-es" /></Field>
              <Field label={t.admin.settingsForm.serviceEn}><Textarea rows={3} value={s.service_times_en} onChange={set("service_times_en")} data-testid="settings-service-en" /></Field>
            </div>
          </Group>
          <Group title="Social">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.instagram}><Input value={s.instagram} onChange={set("instagram")} data-testid="settings-instagram" /></Field>
              <Field label={t.admin.settingsForm.facebook}><Input value={s.facebook} onChange={set("facebook")} data-testid="settings-facebook" /></Field>
              <Field label={t.admin.settingsForm.youtube}><Input value={s.youtube} onChange={set("youtube")} data-testid="settings-youtube" /></Field>
              <Field label={t.admin.settingsForm.tiktok}><Input value={s.tiktok} onChange={set("tiktok")} data-testid="settings-tiktok" /></Field>
              <Field label={t.admin.settingsForm.whatsapp}><Input value={s.whatsapp} onChange={set("whatsapp")} data-testid="settings-whatsapp" /></Field>
            </div>
          </Group>
          <Group title={t.admin.settingsForm.zelleSection}>
            <div className="flex items-center justify-between rounded-xl border border-border bg-white p-3">
              <div>
                <p className="font-medium text-sm">{t.admin.settingsForm.zelleEnabled}</p>
                <p className="text-xs text-muted-foreground">Mostrar tab Zelle en la pagina Ofrendar.</p>
              </div>
              <Switch checked={!!s.zelle_enabled} onCheckedChange={setBool("zelle_enabled")} data-testid="settings-zelle-enabled" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.zellePhone}><Input value={s.zelle_phone} onChange={set("zelle_phone")} data-testid="settings-zelle-phone" placeholder="(323) 573-3889" /></Field>
              <Field label={t.admin.settingsForm.zelleEmail}><Input value={s.zelle_email} onChange={set("zelle_email")} data-testid="settings-zelle-email" placeholder="" /></Field>
              <Field label={t.admin.settingsForm.zelleName}><Input value={s.zelle_name} onChange={set("zelle_name")} data-testid="settings-zelle-name" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.zelleInstructionsEs}><Textarea rows={2} value={s.zelle_instructions_es} onChange={set("zelle_instructions_es")} data-testid="settings-zelle-inst-es" /></Field>
              <Field label={t.admin.settingsForm.zelleInstructionsEn}><Textarea rows={2} value={s.zelle_instructions_en} onChange={set("zelle_instructions_en")} data-testid="settings-zelle-inst-en" /></Field>
            </div>
          </Group>
          <Group title={t.admin.settingsForm.stripeSection}>
            <div className="flex items-center justify-between rounded-xl border border-border bg-white p-3">
              <div>
                <p className="font-medium text-sm">{t.admin.settingsForm.stripeEnabled}</p>
                <p className="text-xs text-muted-foreground">Habilita el boton de tarjeta (Stripe Checkout).</p>
              </div>
              <Switch checked={!!s.stripe_enabled} onCheckedChange={setBool("stripe_enabled")} data-testid="settings-stripe-enabled" />
            </div>
          </Group>
          <Group title={t.admin.settingsForm.notificationsSection}>
            <Field label={t.admin.settingsForm.notificationRecipients}>
              <Input
                type="text"
                inputMode="email"
                placeholder="pastor@iglesia.com, oficina@iglesia.com"
                value={s.notification_recipients || ""}
                onChange={set("notification_recipients")}
                data-testid="settings-notification-recipients"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t.admin.settingsForm.notificationRecipientsHint}</p>
            </Field>
          </Group>
          <Group title={t.admin.settingsForm.surveySection}>
            <div className="flex items-center justify-between rounded-xl border border-border bg-white p-3">
              <div className="min-w-0 pr-2">
                <p className="font-medium text-sm">{t.admin.settingsForm.surveyEnabled}</p>
                <p className="text-xs text-muted-foreground">{t.admin.settingsForm.surveyEnabledHint}</p>
              </div>
              <Switch checked={!!s.survey_enabled} onCheckedChange={setBool("survey_enabled")} data-testid="settings-survey-enabled" />
            </div>
            <Field label={t.admin.settingsForm.surveyUrl}>
              <Input
                type="url"
                inputMode="url"
                placeholder="https://example.questionpro.com/..."
                value={s.survey_url}
                onChange={set("survey_url")}
                data-testid="settings-survey-url"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t.admin.settingsForm.surveyUrlHint}</p>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.surveyTitleEs}>
                <Input value={s.survey_title_es} onChange={set("survey_title_es")} data-testid="settings-survey-title-es" />
              </Field>
              <Field label={t.admin.settingsForm.surveyTitleEn}>
                <Input value={s.survey_title_en} onChange={set("survey_title_en")} data-testid="settings-survey-title-en" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.surveyDescriptionEs}>
                <Textarea rows={2} value={s.survey_description_es} onChange={set("survey_description_es")} data-testid="settings-survey-desc-es" />
              </Field>
              <Field label={t.admin.settingsForm.surveyDescriptionEn}>
                <Textarea rows={2} value={s.survey_description_en} onChange={set("survey_description_en")} data-testid="settings-survey-desc-en" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t.admin.settingsForm.surveyCtaEs}>
                <Input value={s.survey_cta_es} onChange={set("survey_cta_es")} placeholder="Tomar encuesta" data-testid="settings-survey-cta-es" />
              </Field>
              <Field label={t.admin.settingsForm.surveyCtaEn}>
                <Input value={s.survey_cta_en} onChange={set("survey_cta_en")} placeholder="Take the survey" data-testid="settings-survey-cta-en" />
              </Field>
            </div>
          </Group>
          <div className="pt-2">
            <Button onClick={onSave} disabled={saving} data-testid="admin-settings-save">
              <Save className="mr-1 h-4 w-4" />{saving ? "..." : t.common.save}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }) { return (<div className="space-y-1.5"><Label className="text-xs font-medium">{label}</Label>{children}</div>); }
function Group({ title, children }) { return (<section className="space-y-3"><p className="font-serif-display text-base font-semibold">{title}</p>{children}</section>); }
